import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { useParams, useRouter } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { ErrorBoundary as ReactErrorBoundary, useErrorBoundary } from 'react-error-boundary'
import { Button } from '../../rapid-ui/button'
import { logger } from '../../utilities'

/**
 * Parse debugId to extract component type and name
 * @param debugId - Add postfix to identify the component in error boundaries (e.g., "review-panel", "feed-simulator")
 */
function parseDebugId(debugId?: string): { componentType: string; componentName: string } {
	if (!debugId) { return { componentType: 'unknown', componentName: 'unknown' } }

	/**
	 * @todo: Further improve this but with less hardcoding
	 */
	if (debugId.endsWith('-panel')) {
		return {
			componentType: 'panel',
			componentName: debugId.replace('-panel', ''),
		}
	}

	return {
		componentType: 'simulator',
		componentName: debugId,
	}
}

export function EditorErrorBoundary({ children, debugId }: { children: React.ReactNode; debugId?: string }) {
	const { user, isLoading } = useAuth()
	const organizationId = useJobillaOrgId()
	const router = useRouter()
	const params = useParams({ strict: false })

	return (
		<ReactErrorBoundary
			fallbackRender={({ error }) => <ErrorFallback error={error} />}
			onError={(error, info) => {
				const { componentType, componentName } = parseDebugId(debugId)

				const currentPath = router.state.location.pathname
				const editorMode = currentPath.includes('/campaign/new') ? 'new' : 'edit'

				const campaignId = (params as { campaignId?: string }).campaignId

				if (!isLoading && user) {
					const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null
					logger.identify({
						id: user.id,
						email: user.email,
						username: fullName || undefined,
					})
				}

				logger.error(error, {
					tags: {
						error_boundary: debugId || 'unknown',
						component_type: componentType,
						component_name: componentName,
						editor_section: componentType === 'panel' ? 'sidebar' : 'simulators',
						editor_mode: editorMode,
						has_campaign_id: campaignId ? 'yes' : 'no',
						organization_id: organizationId ?? 'unknown',
					},
					data: {
						errorBoundary: {
							componentStack: info.componentStack,
							debugId: debugId || 'unknown',
							componentType,
							componentName,
						},
						editor: {
							mode: editorMode,
							campaignId: campaignId || 'none',
						},
						organization: organizationId
							? { id: organizationId }
							: undefined,
					},
				})
			}}
		>
			{children}
		</ReactErrorBoundary>
	)
}

function ErrorFallback({ error }: { error: Error | null }) {
	const { resetBoundary } = useErrorBoundary()

	return (
		<div className="flex items-center justify-center min-h-[400px] p-6">
			<motion.div
				initial={{ opacity: 0, translateY: 10 }}
				animate={{ opacity: 1, translateY: 0 }}
				transition={{ duration: 0.3 }}
				className="max-w-2xl w-full"
			>
				<div className="ring ring-red-200 p-6 rounded-md shadow-lg bg-white">
					<div className="text-red-600 text-2xl font-medium mb-2">
						Something went wrong
					</div>
					<div className="text-sm text-grey-700 mb-4">
						The editor encountered an unexpected error. You can try resetting this component, or refresh the
						page if the problem persists.
					</div>

					{error?.message && (
						<div className="mt-4 mb-6 p-3 bg-grey-50 rounded border border-grey-100">
							<div className="font-mono text-xs text-red-600 break-words">
								{error.message}
							</div>
						</div>
					)}

					<div className="flex gap-3">
						<Button type="neutral" prefix="refresh" onClick={resetBoundary}>
							Reset Component
						</Button>
						<Button
							type="subtle"
							onClick={() => window.location.reload()}
						>
							Refresh Page
						</Button>
					</div>
				</div>
			</motion.div>
		</div>
	)
}
